import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Header from '@/components/Header';
import HeroSection from '@/components/HeroSection';
import ProgressSection from '@/components/ProgressSection';
import MetricsSection from '@/components/MetricsSection';
import PledgeForm from '@/components/PledgeForm';
import PledgeActivity from '@/components/PledgeActivity';
import AllocationBanner from '@/components/AllocationBanner';
import Footer from '@/components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InvestorDashboard() {
  const [stats, setStats] = useState({
    total_pledged: 0,
    number_of_pledges: 0,
    min_target: 1220000,
    max_target: 4500000,
    min_target_percentage: 0,
    max_target_percentage: 0,
    remaining_allocation: 4500000,
    is_min_achieved: false,
    is_nearing_cap: false
  });
  const [pledges, setPledges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError('Failed to load fundraising data');
    }
  }, []);

  const fetchPledges = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/pledges`);
      setPledges(response.data);
    } catch (err) {
      console.error('Error fetching pledges:', err);
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchStats(), fetchPledges()]);
    setLoading(false);
  }, [fetchStats, fetchPledges]);

  useEffect(() => {
    // Initial data load
    let mounted = true;
    
    const loadInitialData = async () => {
      if (mounted) {
        await fetchData();
      }
    };
    
    loadInitialData();
    
    // Poll for updates every 30 seconds
    const interval = setInterval(() => {
      if (mounted) {
        fetchStats();
        fetchPledges();
      }
    }, 30000);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePledgeSuccess = () => {
    // Refresh data after successful pledge
    fetchStats();
    fetchPledges();
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="relative">
        {/* Hero Section */}
        <HeroSection />
        
        {/* Main Content Container */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
          {/* Live Progress Section */}
          <section className="relative -mt-16 z-10 mb-12">
            <ProgressSection stats={stats} loading={loading} />
          </section>
          
          {/* Key Metrics */}
          <section className="mb-12">
            <MetricsSection stats={stats} loading={loading} />
          </section>
          
          {/* Allocation Banner (shown when nearing cap) */}
          {stats.is_nearing_cap && (
            <section className="mb-12">
              <AllocationBanner remainingAllocation={stats.remaining_allocation} />
            </section>
          )}
          
          {/* Two Column Layout: Form + Activity */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Pledge Form */}
            <section id="pledge-form">
              <PledgeForm 
                onSuccess={handlePledgeSuccess} 
                remainingAllocation={stats.remaining_allocation}
                isNearingCap={stats.is_nearing_cap}
              />
            </section>
            
            {/* Pledge Activity */}
            <section>
              <PledgeActivity pledges={pledges} loading={loading} />
            </section>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
